import React, { useEffect, useState } from 'react';
import DocumentService from '../../../Services/documents.service';
import { Modal, TextField, Button, IconButton, Tooltip } from '@mui/material';
import { styled } from '@mui/system';
import SaveIcon from '@mui/icons-material/SaveRounded';
import CancelIcon from '@mui/icons-material/CancelRounded';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const FIELDS = [
  { key: 'taxInvoiceReference', label: 'TaxInvoice' },
  { key: 'recieptTaxReference', label: 'ReceiptTax' },
  { key: 'creditNoteReference', label: 'CreditNote' },
  { key: 'receiptReference', label: 'Receipt' },
  { key: 'quotationReference', label: 'Quotation' },
  { key: 'orderReference', label: 'Order' },
  { key: 'transactionReference', label: 'Transaction' },
  { key: 'postponedRecieptReference', label: 'PostponedReceipt' },
  { key: 'depositReference', label: 'Deposit' },
];

const StyledContainer = styled('div')({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  background: '#fff',
  padding: 24,
  minWidth: 520,
  maxHeight: '80vh',
  width: 500,
  overflowY: 'auto',
  borderRadius: 8,
  boxShadow: '0 4px 10px rgba(0,0,0,.4)',
  border: '1px solid #304fff',
  display: 'flex',
  flexDirection: 'column',
  rowGap: 20,
  direction: 'rtl',
});

const Row = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start',
  columnGap: 32,
});

const inputPropsStyle = {
  disableUnderline: true,
  sx: {
    height: '48px',
    width: '90%',

    padding: '0 10px',
    borderRadius: '8px',
    background: '#F5F5F6',
    border: '1px solid transparent',
    '&:focus-within': { border: '2px solid #304FFF' },
  },
};

const inputStyle = {
  style: {
    textAlign: 'right',
    direction: 'rtl',
  },
};

export default function ReferencesTreeModal({ open, caseId, token, onClose, onSave }) {
  const { t } = useTranslation();
  const [refs, setRefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    DocumentService.GetReferenceByCaseId(caseId, token)
      .then(r => (r.success ? setRefs(r.data) : setRefs({})))
      .catch(() => setRefs({}));
  }, [open, caseId, token]);

  const handleChange = (k, v) => setRefs(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!refs) return;
    setSaving(true);
    try {
      await DocumentService.EditReference(refs, token);
      toast.success(t('ReferencesUpdatedSuccessfully'));
      // Call onSave callback if provided to refresh client data
      if (onSave) {
        await onSave();
      }
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const inputs = FIELDS.map(f => (
    <div key={f.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'right', }}>
      <div style={{ fontWeight: 500, textAlign: 'right' }}>{t(f.label)}</div>
      <TextField
        fullWidth
        type="number"
        value={refs ? refs[f.key] ?? 0 : ''}
        onChange={e => handleChange(f.key, parseInt(e.target.value, 10) || 0)}
        variant="standard"
        InputProps={inputPropsStyle}
        inputProps={inputStyle}
      />
    </div>
  ));

  const rows = [];
  for (let i = 0; i < inputs.length; i += 2) {
    // Always start from the right (Deposit should be on the right)
    const pair = inputs.slice(i, i + 2);
    if (pair.length === 1) pair.unshift(<div key={`empty-${i}`} style={{ flex: 1 }} />);
    rows.push(<Row key={i}>{pair}</Row>);
  }

  return (
    <Modal open={open} onClose={onClose}>
      <StyledContainer>
        {/* Header: Title on right, X icon on left */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Tooltip title={t('Close')}>
            <IconButton onClick={onClose}>
              <CancelIcon style={{ color: '#304FFF' }} />
            </IconButton>
          </Tooltip>
          <div style={{ fontSize: 20, fontWeight: 'bold', textAlign: 'right' }}>{t('References')}</div>
        </div>

        <div style={{ fontSize: 19,fontWeight: '500', color: '#8a0606', marginTop: -12, marginBottom: 4, textAlign: 'right' }}>
          {t('EditReferenceToMatchLastInvoice')}
        </div>

        {/* Content */}
        {!refs ? (
          <div style={{ padding: 24 }}>{t('Loading…')}</div>
        ) : (
          <>
            {rows}

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 8 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{
                  background: '#304FFF',
                  textTransform: 'none',
                  flexDirection: 'reverse',
                  gap: 1,
                }}
              >
                {saving ? t('Saving') : t('Save')}
              </Button>
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{ textTransform: 'none' }}
              >
                {t('Close')}
              </Button>
            </div>
          </>
        )}
      </StyledContainer>
    </Modal>
  );
}
